import { type FC, useCallback, useEffect, useRef } from "react";

import { Box, Flex, Text } from "@mantine/core";

import {
    ExclusiveLensStatus,
    LensCreatorPayoutEligibility,
    type MyLensesLens,
    Visibility,
} from "../../../gql-generated/types";
import { LensCreatorPayout } from "../../../lens-creator-payout/components";
import { ExclusiveLensToggle } from "../../../lens-creator-payout/components/ExclusiveLensToggle";
import { PhoneVerificationModal } from "../../../lens-creator-payout/components/PhoneVerificationModal";
import { RemovedExclusiveLensAlert } from "../../../lens-creator-payout/components/RemovedExclusiveLensAlert";
import { TosModalHeader } from "../../../lens-creator-payout/components/messages.generated";
import { TOS_KEY_MAP, TosModal, TosSource } from "../../../shared/components/tos";
import {
    useCreatorVerification,
    useMonetizationState,
    useMonetizationTos,
    usePhoneVerificationGate,
} from "../../../shared/hooks";
import { type SubmissionQuota } from "../../../shared/models/SubmissionQuota";
import { type LensPreviewMediaItem } from "../../../shared/state";
import { getExclusiveLensFlags } from "../../../shared/utils/monetizationUtils";
import { isSubmissionQuotaExhaustedForEnforcement } from "../../../shared/utils/submissionQuota";

import styles from "./MonetizationSection.module.css";
import { MonetizationTosWarning } from "./MonetizationTosWarning";
import {
    AiClipsLensInfo,
    LensCreatorRewardsOption,
    LensPlusPilotProgramOption,
    MonetizationDisabledMessage,
} from "./messages.generated";

interface MonetizationModalSectionProps {
    // LensCreatorPayout props
    lensCreatorPayoutEnrolled?: boolean;
    setLensCreatorPayoutEnrolled?: (enrolled: boolean) => void;
    lensForUpdate?: Pick<MyLensesLens, "distributionsList" | "lensCreatorPayoutEligibility">;
    lensCreatorPayoutEligibility?: LensCreatorPayoutEligibility;
    visibility?: Visibility;
    lensCreationWithinThreshold?: boolean;
    blockUnenrollment?: boolean;

    // ExclusiveLensToggle props
    hasMusic?: boolean;
    hasLyrics?: boolean;
    previewMediaItems: LensPreviewMediaItem[];
    exclusiveLensStatus: ExclusiveLensStatus;
    onExclusiveLensChange?: (isExclusive: boolean) => void;
    canToggleExclusiveLens?: boolean;

    // Shared props
    disabled?: boolean;
    isExclusiveLensFeatureEnabled: boolean;
    isMyLensesCreatorPayoutEnabled: boolean;
    isExclusiveLensChecked?: boolean;
    isAIClip?: boolean;
    lensPlusSubmissionQuota?: SubmissionQuota | null;
}

export const MonetizationSection: FC<MonetizationModalSectionProps> = ({
    // LensCreatorPayout props
    lensCreatorPayoutEnrolled,
    setLensCreatorPayoutEnrolled,
    lensForUpdate,
    lensCreatorPayoutEligibility,
    visibility,
    lensCreationWithinThreshold,
    blockUnenrollment = false,

    // ExclusiveLensToggle props
    hasMusic,
    hasLyrics,
    previewMediaItems,
    exclusiveLensStatus,
    onExclusiveLensChange,
    canToggleExclusiveLens,

    // Shared props
    disabled = false,
    isExclusiveLensFeatureEnabled,
    isMyLensesCreatorPayoutEnabled,
    isExclusiveLensChecked,
    isAIClip = false,
    lensPlusSubmissionQuota,
}) => {
    const {
        loading: tosLoading,
        tos,
        acceptedLatestVersion,
        acceptedOldVersion,
        tosModalVisible,
        tosSource,
        triggerTosModal,
        closeTosModal,
        triggerTosAcceptance,
    } = useMonetizationTos();

    const { ownerMismatch, userLoading, isUserEligibleForExclusiveLens } = useMonetizationState({
        lensForUpdate,
    });

    // Owned here rather than in ExclusiveLensToggle: this is the only surface that can
    // disable BOTH enrollment controls while the check is in flight.
    const { required, checkHasVerifiedPhone } = useCreatorVerification();
    const { phoneModalProps, isCheckingPhone, requireVerifiedPhone } = usePhoneVerificationGate(
        required,
        checkHasVerifiedPhone,
    );

    const { isExclusiveLens, isExclusiveLensPending, hasExclusiveLensProgram } = getExclusiveLensFlags(
        exclusiveLensStatus,
        isExclusiveLensChecked,
    );
    const isRemovedFromLensPlus = exclusiveLensStatus === ExclusiveLensStatus.ExclusiveLensStatusRemoved;
    const isLensPlusQuotaExhausted =
        !lensForUpdate && isSubmissionQuotaExhaustedForEnforcement(lensPlusSubmissionQuota);

    // Read after the phone check resolves instead of the value captured at click time:
    // Lens+ and Top Performer stay mutually exclusive on their own, rather than because
    // isCheckingPhone happened to disable the controls.
    const payoutEnrolledRef = useRef(lensCreatorPayoutEnrolled);
    useEffect(() => {
        payoutEnrolledRef.current = lensCreatorPayoutEnrolled;
    }, [lensCreatorPayoutEnrolled]);

    const handleExclusiveLensChange = useCallback(
        async (isExclusive: boolean) => {
            if (isExclusive && isLensPlusQuotaExhausted) {
                return;
            }
            if (!(await requireVerifiedPhone(isExclusive))) {
                return;
            }
            // Top Performer was enrolled while the check ran: that later choice wins.
            if (isExclusive && payoutEnrolledRef.current) {
                return;
            }
            if (isExclusive && lensCreatorPayoutEnrolled && setLensCreatorPayoutEnrolled) {
                setLensCreatorPayoutEnrolled(false);
            }
            onExclusiveLensChange?.(isExclusive);
        },
        [
            onExclusiveLensChange,
            lensCreatorPayoutEnrolled,
            setLensCreatorPayoutEnrolled,
            isLensPlusQuotaExhausted,
            requireVerifiedPhone,
        ],
    );

    const handleCreatorPayoutChange = useCallback(
        (enrolled: boolean) => {
            if (enrolled && hasExclusiveLensProgram && onExclusiveLensChange) {
                onExclusiveLensChange(false);
            }
            if (setLensCreatorPayoutEnrolled) {
                setLensCreatorPayoutEnrolled(enrolled);
            }
        },
        [setLensCreatorPayoutEnrolled, hasExclusiveLensProgram, onExclusiveLensChange],
    );

    // Enhanced TOS acceptance that enables the specific feature that triggered it
    const handleTosAcceptance = useCallback(async () => {
        try {
            await triggerTosAcceptance();

            // Only enable the specific feature that triggered the TOS modal
            if (
                tosSource === TosSource.CreatorPayout &&
                !disabled &&
                setLensCreatorPayoutEnrolled &&
                !lensCreatorPayoutEnrolled
            ) {
                setLensCreatorPayoutEnrolled(true);
            }
            if (
                tosSource === TosSource.ExclusiveLens &&
                !disabled &&
                !isLensPlusQuotaExhausted &&
                onExclusiveLensChange
            ) {
                if (!(await requireVerifiedPhone(true))) {
                    return;
                }
                onExclusiveLensChange(true);
            }
        } catch (error) {
            console.error("Failed to accept TOS:", error);
        }
    }, [
        triggerTosAcceptance,
        tosSource,
        disabled,
        setLensCreatorPayoutEnrolled,
        lensCreatorPayoutEnrolled,
        onExclusiveLensChange,
        isLensPlusQuotaExhausted,
        requireVerifiedPhone,
    ]);

    // Check if ExclusiveLensToggle should be visible (matches logic in ExclusiveLensToggle.tsx)
    const isExclusiveLensUpdate = lensForUpdate && (isExclusiveLens || isExclusiveLensPending);
    const shouldShowExclusiveLensToggle = !lensForUpdate || isExclusiveLensUpdate;

    const dataLoading = tosLoading || userLoading;

    return (
        <Flex direction="column" gap="md">
            <Box>
                <h3 className="heading-h500">Lens Creator Rewards</h3>
            </Box>
            {isExclusiveLensFeatureEnabled && isRemovedFromLensPlus && <RemovedExclusiveLensAlert />}
            {acceptedLatestVersion ? (
                <Flex direction="column" gap="md" className={styles.container}>
                    {isExclusiveLensFeatureEnabled && !isRemovedFromLensPlus && (
                        <Box>
                            {isAIClip && (
                                <Text size="sm" c="dimmed" px="lg" py="xs">
                                    <AiClipsLensInfo />
                                </Text>
                            )}
                            <ExclusiveLensToggle
                                hasMusic={hasMusic}
                                hasLyrics={hasLyrics}
                                previewMediaItems={previewMediaItems}
                                lens={lensForUpdate}
                                exclusiveLensStatus={exclusiveLensStatus}
                                onExclusiveLensChange={(isExclusive: boolean) => {
                                    void handleExclusiveLensChange(isExclusive);
                                }}
                                disabled={disabled || isCheckingPhone || !!lensCreatorPayoutEnrolled}
                                acceptedLatestVersion={acceptedLatestVersion}
                                acceptedOldVersion={acceptedOldVersion}
                                tosLoading={dataLoading}
                                triggerTosModal={() => {
                                    triggerTosModal(TosSource.ExclusiveLens);
                                }}
                                canToggleExclusiveLens={canToggleExclusiveLens && !lensCreatorPayoutEnrolled}
                                isExclusiveChecked={isExclusiveLensChecked}
                                visibility={visibility}
                                lensPlusSubmissionQuota={lensPlusSubmissionQuota}
                            />
                            {isUserEligibleForExclusiveLens &&
                                lensCreatorPayoutEnrolled &&
                                shouldShowExclusiveLensToggle && (
                                    <Text size="xs" c="dimmed" px="lg">
                                        <MonetizationDisabledMessage conflictingOption={<LensCreatorRewardsOption />} />
                                    </Text>
                                )}
                        </Box>
                    )}
                    {isMyLensesCreatorPayoutEnabled && (
                        <Box>
                            <LensCreatorPayout
                                lensCreatorPayoutEnrolled={!!lensCreatorPayoutEnrolled}
                                setLensCreatorPayoutEnrolled={handleCreatorPayoutChange}
                                lensCreatorPayoutEligibility={
                                    lensForUpdate?.lensCreatorPayoutEligibility ?? lensCreatorPayoutEligibility
                                }
                                ownerMismatch={ownerMismatch}
                                blockUnenrollment={blockUnenrollment}
                                disabled={
                                    disabled ||
                                    isCheckingPhone ||
                                    visibility === Visibility.VisibilityDontPublishToSnapchat ||
                                    hasExclusiveLensProgram ||
                                    isAIClip ||
                                    ((lensForUpdate?.lensCreatorPayoutEligibility ?? lensCreatorPayoutEligibility) &&
                                        (lensForUpdate?.lensCreatorPayoutEligibility ??
                                            lensCreatorPayoutEligibility) !==
                                            LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityUnset)
                                }
                                lensCreationWithinThreshold={lensCreationWithinThreshold}
                                loading={dataLoading}
                                triggerTosModal={() => {
                                    triggerTosModal(TosSource.CreatorPayout);
                                }}
                            />
                            {hasExclusiveLensProgram && (
                                <Text size="xs" c="dimmed" px="xl" mb="md">
                                    <MonetizationDisabledMessage conflictingOption={<LensPlusPilotProgramOption />} />
                                </Text>
                            )}
                        </Box>
                    )}
                </Flex>
            ) : (
                <MonetizationTosWarning
                    dataLoading={dataLoading}
                    triggerTosModal={() => {
                        triggerTosModal(TosSource.CreatorPayout);
                    }}
                />
            )}
            {/* Shared TOS Modal */}
            <TosModal
                tosHeading={<TosModalHeader />}
                tosMetadata={tos?.metadata ?? undefined}
                visible={tosModalVisible}
                disabled={dataLoading}
                loading={dataLoading}
                accepted={acceptedLatestVersion}
                onAccept={handleTosAcceptance}
                onDismiss={closeTosModal}
                initialStepKey={tosSource ? TOS_KEY_MAP[tosSource] : undefined}
            />
            <PhoneVerificationModal modalProps={phoneModalProps} />
        </Flex>
    );
};
